import { useEffect, useRef } from 'react';
import { cn } from './cn';

interface Star {
  x: number;
  y: number;
  r: number;
  phase: number;
  speed: number;
}

/**
 * A faint field of twinkling stars filling its (relatively positioned) parent. Decorative: drawn
 * on a canvas at ~12 fps, paused while off-screen or hidden, and static under reduced motion.
 */
export function Starfield({ density = 0.00018, color = '31, 58, 82', opacity = 0.5, className }: { density?: number; color?: string; opacity?: number; className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let stars: Star[] = [];
    let frame = 0;
    let last = 0;
    let visible = true;

    const layout = () => {
      const ratio = window.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.round(width * ratio));
      canvas.height = Math.max(1, Math.round(height * ratio));
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const count = Math.min(160, Math.round(width * height * density));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() < 0.85 ? 0.6 + Math.random() * 0.5 : 1.2 + Math.random() * 0.6,
        phase: Math.random() * Math.PI * 2,
        speed: 0.6 + Math.random() * 1.4,
      }));
    };

    const draw = (t: number) => {
      const { width, height } = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, width, height);
      for (const s of stars) {
        const twinkle = still ? 0.7 : 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(s.phase + (t / 1000) * s.speed));
        ctx.fillStyle = `rgba(${color}, ${twinkle})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const tick = (t: number) => {
      frame = requestAnimationFrame(tick);
      if (!visible || document.hidden || t - last < 80) return;
      last = t;
      draw(t);
    };

    layout();
    draw(0);
    const resize = new ResizeObserver(() => {
      layout();
      draw(performance.now());
    });
    resize.observe(canvas);
    const seen = new IntersectionObserver(([entry]) => {
      visible = !!entry?.isIntersecting;
    });
    seen.observe(canvas);
    if (!still) frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      seen.disconnect();
    };
  }, [density, color]);

  return <canvas ref={ref} aria-hidden className={cn('pointer-events-none absolute inset-0 size-full', className)} style={{ opacity }} />;
}
